"use client";
import { useActionState, useState } from "react";
import { GoogleIcon } from "@/components/AuthShell";
import { signUpAction, logInAction, forgotAction, resetAction, type FormState } from "./actions";

function PasswordField({ name = "password", autoComplete }: { name?: string; autoComplete: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input id={name} name={name} type={show ? "text" : "password"} required minLength={8} autoComplete={autoComplete} className="field pr-12" placeholder="At least 8 characters" />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3.5 top-0 h-12 text-sm font-medium text-muted hover:text-ink" aria-label={show ? "Hide password" : "Show password"}>
        {show ? "Hide" : "Show"}
      </button>
    </div>
  );
}

function Social({ google, mode }: { google: boolean; mode: "signup" | "login" }) {
  if (!google) return null;
  return (
    <>
      <a href={`/api/auth/google/start?mode=${mode}`} className="btn-outline w-full shadow-sm"><GoogleIcon /> Continue with Google</a>
      <div className="flex items-center gap-4 my-7 text-xs font-medium uppercase tracking-wider text-muted"><span className="h-px flex-1 bg-line" />or<span className="h-px flex-1 bg-line" /></div>
    </>
  );
}

const Error = ({ s }: { s: FormState }) => (s.error ? <p role="alert" className="rounded-xl bg-red-50 border border-red-100 px-3.5 py-2.5 text-sm text-red-700">{s.error}</p> : null);

export function SignUpForm({ google }: { google: boolean }) {
  const [s, act, pending] = useActionState(signUpAction, {});
  return (
    <div>
      <Social google={google} mode="signup" />
      <form action={act} className="space-y-4">
        <div><label className="label" htmlFor="email">Email</label><input id="email" name="email" type="email" required autoComplete="email" className="field" placeholder="you@example.com" defaultValue={s.email ?? ""} /></div>
        <div><label className="label" htmlFor="password">Password</label><PasswordField autoComplete="new-password" /></div>
        <Error s={s} />
        <button className="btn-primary w-full shadow-sm" disabled={pending}>{pending ? "Creating your account…" : "Sign up with email"}</button>
      </form>
      <p className="mt-5 text-[13px] leading-relaxed text-muted">By continuing with Google or email, you agree to Lifeable's <a href="/terms" className="text-indigo underline">Terms of Service</a> and <a href="/privacy" className="text-indigo underline">Privacy Policy</a>.</p>
      <p className="mt-8 pt-6 border-t border-line text-sm text-center text-muted">Already have an account? <a href="/login" className="font-medium text-indigo hover:underline">Log in</a></p>
    </div>
  );
}

export function LogInForm({ google, next }: { google: boolean; next?: string }) {
  const [s, act, pending] = useActionState(logInAction, {});
  return (
    <div>
      <Social google={google} mode="login" />
      <form action={act} className="space-y-4">
        <input type="hidden" name="next" value={next ?? ""} />
        <div><label className="label" htmlFor="email">Email</label><input id="email" name="email" type="email" required autoComplete="email" className="field" placeholder="you@example.com" defaultValue={s.email ?? ""} /></div>
        <div>
          <div className="flex items-center justify-between"><label className="label" htmlFor="password">Password</label><a href="/forgot" className="text-sm font-medium text-indigo mb-1.5 hover:underline">Forgot?</a></div>
          <PasswordField autoComplete="current-password" />
        </div>
        <Error s={s} />
        <button className="btn-primary w-full shadow-sm" disabled={pending}>{pending ? "Logging in…" : "Log in"}</button>
      </form>
      <p className="mt-8 pt-6 border-t border-line text-sm text-center text-muted">New to Lifeable? <a href="/signup" className="font-medium text-indigo hover:underline">Sign up</a></p>
    </div>
  );
}

export function ForgotForm() {
  const [s, act, pending] = useActionState(forgotAction, {});
  return (
    <form action={act} className="space-y-4">
      <p className="text-muted">Enter your email and we'll send a link to choose a new password.</p>
      <div><label className="label" htmlFor="email">Email</label><input id="email" name="email" type="email" required autoComplete="email" className="field" /></div>
      {s.done ? <p className="text-sm text-ink">{s.done}</p> : null}
      <button className="btn-primary w-full" disabled={pending || !!s.done}>Send reset link</button>
      <p className="text-sm text-center text-muted"><a href="/login" className="text-indigo underline">Back to log in</a></p>
    </form>
  );
}

export function ResetForm({ token }: { token: string }) {
  const [s, act, pending] = useActionState(resetAction, {});
  return (
    <form action={act} className="space-y-4">
      <input type="hidden" name="token" value={token} />
      <div><label className="label" htmlFor="password">New password</label><PasswordField autoComplete="new-password" /></div>
      <Error s={s} />
      <button className="btn-primary w-full" disabled={pending}>Set new password</button>
    </form>
  );
}
